import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * Data payload for updating a Confluence page.
 */
export type ConfluenceUpdatePageUsingToolCallsData = {
	/**
	 * The tools and modifications to be applied to the page.
	 */
	toolCalls: string;
	/**
	 * The content id of the page to be updated.
	 */
	contentId: string;
};

/**
 * Data payload for updating a Dynamic UI on a Confluence page.
 */
export type ConfluenceUpdateDynamicUiOnPageData = {
	/**
	 * The Maui ID of the dynamic UI to be updated.
	 */
	id: string;
	/**
	 * The previous Maui ID of the dynamic UI.
	 */
	previousMauiId: string;
};

/**
 * Data payload for updating an image on a Confluence page.
 */
export type ConfluenceUpdateImageOnPageData = {
	/**
	 * The media file id of the image to be updated.
	 */
	mediaFileId: string;
	/**
	 * The previous media file id of the image.
	 */
	previousMediaFileId: string;
	/**
	 * A signed URL pointing to the new image binary in the MAUI personal collection..
	 */
	mediaUrl: string;
	/**
	 * The MAUI personal collection the new image lives in.
	 */
	mediaCollection: string;
};

/**
 * Data payload for setting action classification on a Confluence page.
 */
export type ConfluenceSetActionClassificationMetadata = {
	/**
	 * The action classification to be stored.
	 */
	promptVersion: string;
};

/**
 * Properties for the Confluence update page command.
 *
 * @extends CommandProperties
 */
export interface ConfluenceUpdatePageCommandBaseProperties extends CommandProperties {
	/**
	 * Whether the stream is complete.
	 */
	isStreamComplete: boolean;
	/**
	 * Trace id of the request
	 */
	traceId?: string;
	/**
	 * Request id for E2E event tracking and request correlation
	 */
	requestId?: string;
	/**
	 * Single instrumentation id of the request
	 * This is used to align events between rovo and the page command that may have
	 * actioned / dismissed the command
	 */
	singleInstrumentationId: string | undefined;
}

export type ConfluenceUpdatePageCommandProperties = ConfluenceUpdatePageCommandBaseProperties &
	(
		| {
				key: 'aifc-adf-update';
				data: ConfluenceUpdatePageUsingToolCallsData;
		  }
		| {
				key: 'dynamic-ui-create';
				data: ConfluenceUpdateDynamicUiOnPageData;
		  }
		| {
				key: 'image-create';
				data: ConfluenceUpdateImageOnPageData;
		  }
		| {
				key: 'aifc-action-classification-metadata';
				data: ConfluenceSetActionClassificationMetadata;
		  }
	);

/**
 * A command sent by a bridge API client to update a Confluence page.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to create a Confluence page with the specified content.
 */
export const ConfluenceUpdatePageCommand: CommandConstructor<
	ConfluenceUpdatePageCommandProperties,
	SerializableValue
> = createCommand<ConfluenceUpdatePageCommandProperties>('confluence-page-update-command');
